import { useTranslation } from "react-i18next";

import { formatMonthShort, formatNumber } from "@/lib/format";
import { useAnalytics } from "@/features/bots/hooks";

export function AnalyticsPage() {
  const { t } = useTranslation(["common"]);
  const { data, isLoading, isError } = useAnalytics();

  if (isLoading) return <p className="text-sm text-slate-500">{t("common:states.loading")}</p>;
  if (isError || !data) return <p className="text-sm text-red-600">{t("common:analytics.loadingError")}</p>;

  const max = Math.max(1, ...data.months.map((m) => m.messages));

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t("common:analytics.title")}</h1>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {(
          [
            [t("common:analytics.cards.conversations"), data.current.conversations],
            [t("common:analytics.cards.messages"), data.current.messages],
            [t("common:analytics.cards.aiCalls"), data.current.ai_calls],
          ] as const
        ).map(([label, value]) => (
          <div key={label} className="card">
            <div className="card-body">
              <p className="text-xs uppercase text-slate-500">{label}</p>
              <p className="mt-2 text-3xl font-semibold">{formatNumber(value)}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="text-base font-semibold">{t("common:analytics.messagesChartTitle")}</h2>
        </div>
        <div className="card-body">
          {data.months.length === 0 ? (
            <p className="text-sm text-slate-400">{t("common:analytics.noUsage")}</p>
          ) : (
            <div className="flex h-48 items-end gap-3">
              {data.months
                .slice()
                .reverse()
                .map((m) => (
                  <div key={m.period_start} className="flex flex-1 flex-col items-center gap-1">
                    <span className="text-xs font-medium text-slate-600">{formatNumber(m.messages)}</span>
                    <div
                      className="w-full rounded-t-md bg-brand-500"
                      style={{ height: `${(m.messages / max) * 100}%` }}
                    />
                    <span className="text-[10px] text-slate-400">{formatMonthShort(m.period_start)}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
